import React from "react";
import { motion, Variants } from "framer-motion";
import { useLanguage } from "../../i18n/LanguageContext";
import { profile } from "../../data/profile";
import { Reveal } from "../common/Reveal";
import { Terminal } from "../common/Terminal";
import { smoothScrollToId } from "../../utils/smoothScroll";
import "./Hero.css";

const easePremium: [number, number, number, number] = [0.16, 1, 0.3, 1];

const container: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.15 },
  },
};

const item: Variants = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: easePremium },
  },
};

export function Hero() {
  const { t } = useLanguage();

  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    smoothScrollToId(id);
  };

  return (
    <section id="about" className="hero">
      <div className="hero__ambience" aria-hidden="true">
        <div className="bg-grid" />
        <div className="hero__glow hero__glow--a" />
        <div className="hero__glow hero__glow--b" />
        <span className="hero__particle hero__particle--1" />
        <span className="hero__particle hero__particle--2" />
        <span className="hero__particle hero__particle--3" />
        <span className="hero__particle hero__particle--4" />
      </div>

      <div className="container hero__inner">
        <motion.div className="hero__content" variants={container} initial="hidden" animate="visible">
          <motion.h1 className="hero__name" variants={item}>
            {profile.name}
          </motion.h1>
          <motion.span className="hero__role eyebrow" variants={item}>
            {t.hero.eyebrow}
          </motion.span>

          <motion.div className="hero__ctas" variants={item}>
            <a href="#projects" className="btn btn-primary" onClick={scrollTo("projects")} data-cursor="hover">
              {t.hero.ctaPrimary}
            </a>
            <a href="#contact" className="btn btn-secondary" onClick={scrollTo("contact")} data-cursor="hover">
              {t.hero.ctaSecondary}
            </a>
            <a
              href={profile.resumeUrl}
              className="btn btn-ghost"
              target="_blank"
              rel="noopener noreferrer"
              data-cursor="hover"
            >
              {t.hero.ctaResume}
            </a>
          </motion.div>

          <motion.dl className="hero__stats" variants={item}>
            <div className="hero__stat">
              <dt>{t.hero.statLabels.location}</dt>
              <dd className="mono">{t.hero.statValues.location}</dd>
            </div>
            <div className="hero__stat">
              <dt>{t.hero.statLabels.languages}</dt>
              <dd className="mono">{t.hero.statValues.languages}</dd>
            </div>
            <div className="hero__stat">
              <dt>{t.hero.statLabels.availability}</dt>
              <dd className="mono">{t.hero.statValues.availability}</dd>
            </div>
          </motion.dl>

          <div className="hero__about">
            <Reveal direction="up">
              <span className="eyebrow">{t.hero.aboutEyebrow}</span>
              <h2 className="hero__about-heading">{t.hero.aboutHeading}</h2>
            </Reveal>

            <Reveal direction="up" delay={0.1}>
              <p className="hero__summary">{t.hero.subtitle}</p>
            </Reveal>

            <div className="hero__paragraphs">
              {t.hero.paragraphs.map((p, i) => (
                <Reveal direction="up" delay={0.06 * (i + 1)} key={i}>
                  <p className="hero__paragraph">{p}</p>
                </Reveal>
              ))}
            </div>

            <Reveal direction="up" delay={0.3}>
              <div className="hero__philosophy">
                <span className="hero__philosophy-label mono">{t.hero.philosophyLabel}</span>
                <p className="hero__philosophy-text">&ldquo;{t.hero.philosophy}&rdquo;</p>
              </div>
            </Reveal>

            <Reveal direction="up" delay={0.36} className="hero__terminal-wrap">
              <Terminal />
            </Reveal>
          </div>
        </motion.div>

        <div className="hero__cards">
          <Reveal direction="up">
            <span className="hero__cards-label mono">{t.hero.focusLabel}</span>
          </Reveal>
          <div className="hero__cards-grid">
            {t.hero.focusAreas.map((area, i) => (
              <motion.div
                className="focus-card"
                key={area.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: easePremium }}
              >
                <span className="focus-card__index mono">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="focus-card__title">{area.title}</h3>
                <p className="focus-card__desc">{area.description}</p>
                <span className="focus-card__line" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <motion.a
        href="#skills"
        className="hero__scroll-hint"
        onClick={scrollTo("skills")}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        data-cursor="hover"
      >
        <span className="mono">{t.hero.scrollHint}</span>
        <span className="hero__scroll-line" />
      </motion.a>
    </section>
  );
}
